import { useEffect, useState } from 'react';
import {
  useGetGradingScaleQuery,
  useSetGradingScaleMutation,
  getApiError,
  type GradeBand,
} from '@/store/api';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { GradeBandsEditor, bandsAreUsable, sortBands } from './GradeBandsEditor';
import { cn } from '@/lib/cn';
import { styles } from '@/styles';

/*
  ═════════════════════════════════════════════════════════════════════════════
  THE SCHOOL'S GRADING SCALE

  The sentence this screen exists to make true
  ────────────────────────────────────────────
  A child who scored 80 last year and was told "A — Excellent" still reads
  A — Excellent on that report card after the school moves to the BECE's 1–9
  in January.

  That holds because every exam takes a COPY of this when it is created and
  keeps it for good. This is a starting point, not a live reference, and the
  screen says so plainly rather than leaving somebody to find out by changing
  it and watching last year's results shift under them.

  Why the upper bound is shown and not typed
  ──────────────────────────────────────────
  Each band is "this score and above", and its ceiling is simply the next band
  minus one. Asking for both ends invites a school to type overlapping or
  gap-ridden ranges, and then a child's mark falls into two grades or none.
  ═════════════════════════════════════════════════════════════════════════════
*/
export function GradingScaleDialog({
  open,
  onClose,
  institutionId,
  canEdit,
}: {
  open: boolean;
  onClose: () => void;
  institutionId: string;
  canEdit: boolean;
}) {
  /*
    No `= []` default here, deliberately.

    A default array literal is a NEW object on every render while the query is
    still loading, so an effect that depends on it re-runs every render — and
    this one calls setState, which triggers the next render. That is an
    infinite loop, and React only tells you about it as "Maximum update depth
    exceeded" from somewhere up the tree. `data` is `undefined` until it
    resolves and a stable reference afterwards, which is exactly what a
    dependency array wants.
  */
  const { data: saved, isLoading } = useGetGradingScaleQuery(institutionId, {
    skip: !institutionId || !open,
  });
  const [setScale, saveState] = useSetGradingScaleMutation();
  const [bands, setBands] = useState<GradeBand[]>([]);

  /* Reset to what is stored each time it opens, so an abandoned edit does not
     come back the next time somebody looks. */
  useEffect(() => {
    if (open && saved) setBands(sortBands(saved));
  }, [open, saved]);

  const sorted = sortBands(bands);
  const dirty = JSON.stringify(sorted) !== JSON.stringify(sortBands(saved ?? []));

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Grading scale"
      description="What NEW exams start from. Exams already created keep the scale they were marked on."
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {canEdit ? 'Cancel' : 'Close'}
          </Button>
          {canEdit && (
            <Button
              variant="primary"
              loading={saveState.isLoading}
              disabled={!dirty || !bandsAreUsable(bands)}
              onClick={() => void setScale({ institutionId, bands: sorted }).then(() => onClose())}
            >
              Save scale
            </Button>
          )}
        </>
      }
    >
      <div className="flex flex-col gap-3">
        {/*
          Stated before anything is changed, not after. The alternative is a
          head teacher adjusting a boundary and wondering all afternoon whether
          they have just re-graded three years of report cards.
        */}
        <p className={cn(styles.meta, 'rounded-md border border-info/25 bg-info/[0.06] px-3 py-2 leading-relaxed')}>
          Changing this affects the <strong className="text-foreground">next</strong> exam you create and
          nothing else. A child graded A last year keeps their A.
        </p>

        {isLoading ? (
          <p className="px-3 py-4 text-center text-[11.5px] text-muted-foreground">Loading…</p>
        ) : (
          <GradeBandsEditor bands={bands} onChange={setBands} canEdit={canEdit} />
        )}

        {saveState.error && (
          <p role="alert" className="text-[11.5px] font-medium text-[hsl(var(--record-ink-danger))]">
            {getApiError(saveState.error)}
          </p>
        )}
      </div>
    </Modal>
  );
}
